/**
 * Clinical AI Agent - Dashboard Tab
 * Displays patient overview, alerts, and recent assessments
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertCircle, Activity, Brain, Thermometer, Heart, Shield, 
  TrendingDown, Clock, AlertTriangle, CheckCircle, Loader2, 
  RefreshCw, Sparkles
} from "lucide-react";
import { SEVERITY_COLORS, getSeverityClass } from "./constants";

export default function DashboardTab({
  dashboard,
  loading,
  isDark,
  onRefresh,
  onRunSafetyCheck,
  onRunCorrelation
}) {
  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <Brain className="h-12 w-12 mx-auto mb-4 text-gray-500" />
        <p className="text-gray-500">No patient selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Patient Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {dashboard.patient_name}
          </h3>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            Phase: {dashboard.current_phase} | Alerts: {dashboard.alert_count}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Alerts */}
      {dashboard.alerts && dashboard.alerts.length > 0 && (
        <Card className={`border ${isDark ? "bg-red-900/20 border-red-500/30" : "bg-red-50 border-red-200"}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Active Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 p-2 rounded ${getSeverityClass(alert.severity)}`}
                >
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{alert.message}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {alert.type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assessment Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        {/* Pain Card */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1">
              <Heart className="h-3 w-3 text-red-400" />
              Pain
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_assessments?.pain?.score !== null ? (
              <div>
                <p className={`text-2xl font-bold ${
                  dashboard.recent_assessments.pain.score >= 7 ? "text-red-400" :
                  dashboard.recent_assessments.pain.score >= 4 ? "text-yellow-400" : "text-green-400"
                }`}>
                  {dashboard.recent_assessments.pain.score}/10
                </p>
                <p className="text-xs text-gray-500">
                  {dashboard.recent_assessments.pain.trend || "No trend"}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No assessment</p>
            )}
          </CardContent>
        </Card>

        {/* Infection Card */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1">
              <Thermometer className="h-3 w-3 text-orange-400" />
              Infection
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_assessments?.infection?.severity ? (
              <div>
                <Badge className={getSeverityClass(dashboard.recent_assessments.infection.severity)}>
                  {dashboard.recent_assessments.infection.severity}
                </Badge>
                <p className="text-xs text-gray-500 mt-1">
                  {dashboard.recent_assessments.infection.status || "Assessed"}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No concerns</p>
            )}
          </CardContent>
        </Card>

        {/* Decline Card */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1">
              <TrendingDown className="h-3 w-3 text-purple-400" />
              Eligibility
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_assessments?.decline?.status ? (
              <div>
                <Badge className={
                  dashboard.recent_assessments.decline.status === "clearly_appropriate" 
                    ? "bg-green-500/20 text-green-400" 
                    : dashboard.recent_assessments.decline.status === "concern_for_discharge"
                    ? "bg-red-500/20 text-red-400"
                    : "bg-yellow-500/20 text-yellow-400"
                }>
                  {dashboard.recent_assessments.decline.status.replace(/_/g, " ")}
                </Badge>
                <p className="text-xs text-gray-500 mt-1">
                  Risk: {dashboard.recent_assessments.decline.risk || "Unknown"}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Not assessed</p>
            )}
          </CardContent>
        </Card>

        {/* Compliance Card */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1">
              <Shield className="h-3 w-3 text-cyan-400" />
              Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_assessments?.compliance?.score !== null ? (
              <div>
                <p className={`text-2xl font-bold ${
                  dashboard.recent_assessments.compliance.score >= 90 ? "text-green-400" :
                  dashboard.recent_assessments.compliance.score >= 70 ? "text-yellow-400" : "text-red-400"
                }`}>
                  {dashboard.recent_assessments.compliance.score}%
                </p>
                <p className="text-xs text-gray-500">
                  {dashboard.recent_assessments.compliance.critical_gaps || 0} critical gaps
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Not checked</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRunSafetyCheck}
              disabled={loading}
              data-testid="run-safety-check-btn"
            >
              <Shield className="h-4 w-4 mr-1" />
              Safety Check
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRunCorrelation}
              disabled={loading}
              data-testid="run-correlation-btn"
            >
              <Activity className="h-4 w-4 mr-1" />
              Correlate Symptoms
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Aide Care Plan Status */}
      {dashboard.aide_care_plan && (
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-400" />
              Aide Care Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm">
                  {dashboard.aide_care_plan.exists ? (
                    <>
                      {dashboard.aide_care_plan.task_count} tasks
                      {dashboard.aide_care_plan.next_review && (
                        <span className="text-gray-500 ml-2">
                          Next review: {new Date(dashboard.aide_care_plan.next_review).toLocaleDateString()}
                        </span>
                      )}
                    </>
                  ) : (
                    "No aide care plan"
                  )}
                </p>
              </div>
              {dashboard.aide_care_plan.exists ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
